import React, { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import api from '../../../../../api';
import PlaidJsonTable from '../../../../../components/PlaidJsonTable';
import PlaidIdentityPanel from './verification/PlaidIdentityPanel';
import CarrierOkPanel from './verification/CarrierOkPanel';
import AvsPanel from './verification/AvsPanel';
import './verification/verification.css';

const carrierContactValues = (values, carrier) => ({
  ...values,
  fmcsaPhoneNumber: values.fmcsaPhoneNumberManualEntry
    ? values.fmcsaPhoneNumber
    : carrier.telephone_number || '',
  fmcsaCellphoneNumber: values.fmcsaCellphoneNumberManualEntry
    ? values.fmcsaCellphoneNumber
    : carrier.cellphone_number || '',
  fmcsaEmail: values.fmcsaEmailManualEntry
    ? values.fmcsaEmail
    : carrier.email_address || '',
});

export const useCarrierVerification = (profileId) => {
  const [verificationResponse, setVerificationResponse] = useState({});
  const [verificationError, setVerificationError] = useState('');
  const formRef = useRef(null);
  const verification =
    verificationResponse.profileId === profileId
      ? verificationResponse.data
      : {};

  useEffect(() => {
    let active = true;
    setVerificationResponse({ profileId, data: {} });
    setVerificationError('');
    async function fetchVerification() {
      try {
        const data = await api.groups.getVerificationInfo(profileId);
        if (active) {
          setVerificationResponse((current) => ({
            profileId,
            data: {
              ...data,
              ...(current.profileId === profileId ? current.data : {}),
            },
          }));
        }
      } catch (error) {
        if (active) {
          setVerificationError(
            'Unable to load verification data. Please reload the profile to try again.'
          );
        }
      }
    }
    fetchVerification();
    return () => {
      active = false;
    };
  }, [profileId]);

  const refreshVerification = (carrier) => {
    const form = formRef.current;
    const values = form && carrierContactValues(form.values, carrier);
    const initialValues =
      form && carrierContactValues(form.initialValues, carrier);

    unstable_batchedUpdates(() => {
      if (form) {
        form.resetForm({ ...form, values: initialValues });
        form.setValues(values, false);
      }
      setVerificationResponse((current) => ({
        profileId,
        data: {
          ...(current.profileId === profileId ? current.data : {}),
          carrierok: carrier,
        },
      }));
    });
  };

  return {
    verification,
    verificationError,
    formRef,
    refreshVerification,
  };
};

const VerificationPanels = ({
  verification = {},
  paymentProfile,
  onCarrierRefresh,
}) => {
  const accounts = verification.plaid?.all_accounts;
  const accountsWithNewer = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    const active = accounts.filter((account) => !account.deleted_reason);
    const defaultAccount = active.find((account) => account.default);
    return active.map((account) => ({
      ...account,
      newer: Boolean(
        !account.default &&
          defaultAccount?.created &&
          new Date(account.created) > new Date(defaultAccount.created)
      ),
    }));
  }, [accounts]);
  const avsEnabled = JSON.parse(process.env.REACT_APP_ENABLE_AVS || false);

  return (
    <>
      <div
        className={`verification-layout${
          avsEnabled ? '' : ' verification-layout-two'
        }`}
      >
        <PlaidIdentityPanel
          verification={verification}
          paymentProfile={paymentProfile}
        />
        <CarrierOkPanel
          verification={verification}
          paymentProfile={paymentProfile}
          onRefresh={onCarrierRefresh}
        />
        {avsEnabled && (
          <AvsPanel
            verification={verification}
            paymentProfile={paymentProfile}
          />
        )}
      </div>
      {accountsWithNewer.length > 0 && (
        <details className="verification-accordion">
          <summary className="verification-accordion-summary">
            Additional payment account data
          </summary>
          <PlaidJsonTable
            title={`Payment accounts (${accountsWithNewer.length})`}
            data={accountsWithNewer}
            omitFields={[
              'id',
              'deleted_by',
              'modified',
              'deleted_reason',
              'newer',
              'factoring_relation_id',
              'card_number',
              'expiration_date',
              'name',
            ]}
          />
        </details>
      )}
    </>
  );
};

export default function VerificationInfo({
  verification,
  paymentProfile = {},
  onCarrierRefresh,
}) {
  return (
    <VerificationPanels
      key={`${paymentProfile.id || ''}:${paymentProfile.user || ''}`}
      verification={verification}
      paymentProfile={paymentProfile}
      onCarrierRefresh={onCarrierRefresh}
    />
  );
}
